import { AdminBulkActions } from '../AdminBulkActions';
import { Pagination } from '../Pagination';
import StatusBadge from '../StatusBadge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Building2, Eye, FileText, Trash2, Undo2, Printer, Pencil, Calculator } from 'lucide-react';
import { REQUEST_THEMES, bankChip, bankLabel } from '../../utils/requestTheme';
import { ExportMenu } from '../ExportMenu';

// Satu komponen untuk dua tabel: Internal (IPR, ungu, BSI/BRI) dan
// Operasional (OPR, maroon, BSI Ops/BRI Ops). Antrean bank tiap varian
// berbeda, dan server juga menolak antrean yang salah.
// Sama dengan APPROVAL_DELETABLE_STATUSES di backend/routes/admin.py.
const APPROVAL_DELETABLE = ['proses_bayar', 'approved', 'rejected', 'submitted'];

export function InternalRequestsTable({ variant = 'internal', appliedSearch, bankSelections, fetchData, filteredInternalRequests, getSelectedForBank, handleBankSelect, handleProcessToBank, handleRevertInternal, internalPage, internalTotal, internalTotalPages, onPageChange, openDeleteDialog, openEditDeductions, openEditDetails, processingBank, setSelectedInternalRequest, setShowInternalDetail, setShowVoucherDialog, setVoucherKeterangan, setVoucherTarget, user }) {
  const theme = REQUEST_THEMES[variant];
  const isOps = variant === 'operasional';
  const tp = theme.testPrefix;
  // testid lama tabel internal dipertahankan agar test UI yang ada tetap jalan.
  const tid = (name) => (isOps ? `${tp}-${name}` : name);
  return (
    <>
      {(user?.role === 'approval' || user?.role === 'admin') && (
        <Card className={theme.card} data-testid={`${tp}-requests-card`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className={`h-5 w-5 ${theme.icon}`} />
                  {theme.title}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${theme.badge}`}>{theme.code}</span>
                </CardTitle>
                <p className="text-sm text-slate-500">{theme.subtitle}</p>
              </div>
              {/* Bank Process Buttons */}
              <div className="flex gap-2">
                {theme.banks.map((b) => (
                  <Button
                    key={b.value}
                    size="sm"
                    className={b.btn}
                    onClick={() => handleProcessToBank(b.value, 'internal')}
                    disabled={processingBank || getSelectedForBank(b.value, 'internal').length === 0}
                    data-testid={`process-${b.value.toLowerCase().replace('_', '-')}-btn`}
                  >
                    <Building2 className="h-4 w-4 mr-1" />
                    Proses {b.ops ? b.bank : b.label}
                    {b.ops && <span className="ml-1 rounded px-1 py-px text-[9px] font-bold tracking-wide bg-white text-slate-800">OPS</span>}
                    <span className="ml-1">({getSelectedForBank(b.value, 'internal').length})</span>
                  </Button>
                ))}
                <ExportMenu table={isOps ? 'operasional-requests' : 'internal-requests'} params={{ search: appliedSearch }} testId={`export-${tp}-requests`} />
                {user?.role === 'admin' && (
                  <AdminBulkActions collectionType={theme.bulkCollection} collectionLabel={theme.bulkLabel} onRefresh={fetchData} />
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredInternalRequests.length > 0 ? (
              <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`${theme.headRow} text-left`}>
                      <th className="px-3 py-3 text-sm font-semibold text-center">
                        <div className="flex flex-col items-center gap-1">
                          {theme.banks.map((b) => <span key={b.value} className={b.text}>{b.label}</span>)}
                        </div>
                      </th>
                      <th className="px-3 py-3 text-sm font-semibold">Request Number</th>
                      <th className="px-3 py-3 text-sm font-semibold">Tanggal Submit</th>
                      <th className="px-3 py-3 text-sm font-semibold">Tipe Request</th>
                      <th className="px-3 py-3 text-sm font-semibold">Perusahaan / Departemen</th>
                      <th className="px-3 py-3 text-sm font-semibold">Amount</th>
                      <th className="px-3 py-3 text-sm font-semibold">Penerima</th>
                      <th className="px-3 py-3 text-sm font-semibold">Status</th>
                      <th className="px-3 py-3 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInternalRequests.map((request) => {
                      const selectionKey = `internal_${request.id}`;
                      const selectedBank = bankSelections[selectionKey];
                      const isAlreadyProcessed = request.bank_selected;
                      // OPR baru bisa dikirim ke bank setelah Approval mengisi
                      // perusahaan (rekening debit export diambil dari sana).
                      const needsCompany = !request.company_id;

                      return (
                        <tr key={request.id} className={`border-b ${isAlreadyProcessed ? 'bg-slate-100 opacity-60' : ''}`} data-testid={`${tp}-request-row-${request.id}`}>
                          <td className="px-3 py-3">
                            {!isAlreadyProcessed && request.status === 'proses_bayar' && needsCompany ? (
                              <span className="text-[11px] text-[#800000] font-semibold text-center block" data-testid={`${tp}-needs-company-${request.id}`}>
                                Isi perusahaan dulu
                              </span>
                            ) : !isAlreadyProcessed && request.status === 'proses_bayar' ? (
                              <div className="flex flex-col items-center gap-2">
                                {theme.banks.map((b) => (
                                  <label key={b.value} className="flex items-center gap-1 cursor-pointer">
                                    <Checkbox
                                      checked={selectedBank === b.value}
                                      onCheckedChange={() => handleBankSelect(request.id, 'internal', b.value)}
                                      className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                      data-testid={`${b.value.toLowerCase().replace('_', '-')}-radio-${request.id}`}
                                    />
                                    <span className={`text-xs font-semibold ${b.text}`}>{b.label}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <span className={`text-xs font-semibold px-2 py-1 rounded ${isAlreadyProcessed ? bankChip(isAlreadyProcessed) : 'text-slate-400'}`}>
                                {isAlreadyProcessed ? bankLabel(isAlreadyProcessed) : '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 font-mono text-sm font-semibold">{request.request_number}</td>
                          <td className="px-3 py-3 text-xs text-slate-600">
                            {request.created_at ? new Date(request.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                            <br />
                            <span className="text-slate-400">{request.created_at ? new Date(request.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </td>
                          <td className="px-3 py-3 text-sm">{request.tipe_request}</td>
                          <td className="px-3 py-3 text-sm">
                            {request.company_name || (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#f6dede] text-[#800000]">Belum diisi</span>
                            )}
                            {request.departemen_nama && <div className="text-xs text-slate-500" data-testid={`dept-${request.id}`}>{request.departemen_nama}</div>}
                          </td>
                          <td className="px-3 py-3 font-mono">Rp {request.amount?.toLocaleString('id-ID')}</td>
                          <td className="px-3 py-3 text-sm">{request.penerima_nama}</td>
                          <td className="px-3 py-3"><StatusBadge status={request.status} /></td>
                          <td className="px-3 py-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedInternalRequest(request); setShowInternalDetail(true); }}
                                data-testid={tid(`view-internal-request-${request.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {/* Print Voucher Pembayaran */}
                              {['approval', 'admin'].includes(user?.role) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-purple-400 text-purple-700 hover:bg-purple-50"
                                  onClick={() => { setVoucherTarget({ type: 'internal', data: request }); setVoucherKeterangan(''); setShowVoucherDialog(true); }}
                                  disabled={needsCompany}
                                  data-testid={tid(`print-voucher-internal-${request.id}`)}
                                  title={needsCompany ? 'Isi perusahaan dulu' : 'Print Voucher Pembayaran'}
                                >
                                  <Printer className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Edit PPh/Admin Bank for internal requests */}
                            {['approval', 'admin'].includes(user?.role) && request.status === 'proses_bayar' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-400 text-blue-700 hover:bg-blue-50"
                                onClick={() => openEditDeductions('internal', request)}
                                data-testid={tid(`edit-deductions-internal-${request.id}`)}
                                title="Edit PPh & Admin Bank"
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Edit Tipe Request & Keterangan for admin/approval */}
                            {['approval', 'admin'].includes(user?.role) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                                onClick={() => openEditDetails(request)}
                                data-testid={tid(`edit-details-internal-${request.id}`)}
                                title={isOps ? 'Edit Perusahaan, Penerima, Tipe & Keterangan' : 'Edit Perusahaan, Tipe Request & Keterangan'}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Revert internal request */}
                            {['approval', 'admin'].includes(user?.role) && ['approved', 'rejected', 'bank_process'].includes(request.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                                onClick={() => handleRevertInternal(request.id)}
                                data-testid={tid(`revert-internal-${request.id}`)}
                                title="Kembalikan ke Proses Bayar"
                              >
                                <Undo2 className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Hapus: admin selalu; approval hanya sebelum masuk Proses Bank (server juga menolak) */}
                            {(user?.role === 'admin' || (user?.role === 'approval' && APPROVAL_DELETABLE.includes(request.status) && !request.bank_selected)) && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openDeleteDialog(request, 'internal_payment_request')}
                                data-testid={tid(`delete-internal-request-${request.id}`)}
                                title="Hapus Request"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={internalPage} totalPages={internalTotalPages} total={internalTotal} onPageChange={onPageChange} />
              </>
            ) : (
              <p className="text-slate-500 text-center py-8">{theme.empty}</p>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
